import React, { lazy, Suspense, useContext, useState, useEffect } from "react";
import firebase from "firebase/compat/app";
import {
  useTheme,
  useMediaQuery,
  AppBar,
  IconButton,
  Toolbar,
  CircularProgress,
  Box,
  Fade,
  Zoom,
  Fab,
  Typography,
  Container,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Divider,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Home,
  Settings,
  Logout,
  Label,
  History,
  Calculate,
  Event,
  Brightness4,
  Brightness7,
} from "@mui/icons-material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { alpha, styled } from "@mui/material/styles";
import { BrowserRouter, Routes, Route, Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import AuthGuard, { UserContext } from "./components/AuthGuard";
import GeneralErrorPage from "./components/GeneralErrorPage";
import { ErrorBoundary } from "react-error-boundary";
import RoleGuard from "./components/guards/RoleGuard";
import { LoggedInRouter } from "./LoggedInRouter";
import { ThemeContext } from "./context/ThemeContext";
import { PlanningRolloverProvider } from "./context/PlanningRolloverContext";
import TimezoneMismatchPrompt from "./components/TimezoneMismatchPrompt";
// Posts removed
import Homepage from "./components/Homepage";
import AdminUser from "./components/admin/AdminUser";
import AdminLog from "./components/admin/AdminLog";
import AdminEmail from "./components/admin/AdminEmail";
// Trim unused admin pages from main app routes

/**
 * Lazy load components to improve performance and reduce initial bundle size
 */
const SignInScreen = lazy(() => import("./components/SignInScreen"));
const TestLogin = lazy(() => import("./components/BrowserAutomationLogin"));
const NotFoundPage = lazy(() => import("./components/NotFoundPage"));

const generalRoutes = [
  {
    path: "/",
    element: <Homepage />,
  },
  {
    path: "/login",
    element: <SignInScreen />,
  },
  {
    path: "/browser-automation-login",
    element: <TestLogin />,
  },
  {
    path: "/admin/users",
    element: (
      <RoleGuard roles={["admin"]}>
        <AdminUser />
      </RoleGuard>
    ),
  },
  {
    path: "/admin/companies",
    element: <NotFoundPage />,
  },
  {
    path: "/admin/quotes",
    element: <NotFoundPage />,
  },
  {
    path: "/admin/email",
    element: (
      <RoleGuard roles={["admin"]}>
        <AdminEmail />
      </RoleGuard>
    ),
  },
  {
    path: "/admin/log",
    element: (
      <RoleGuard roles={["admin"]}>
        <AdminLog />
      </RoleGuard>
    ),
  },
  {
    path: "*",
    element: <NotFoundPage />,
  },
];

const StyledFab = styled(Fab)({
  position: "fixed",
  bottom: 16,
  right: 16,
});

const LoadingScreen = () => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    minHeight="100vh"
  >
    <CircularProgress />
  </Box>
);

const RenderUnauthScreens = () => {
  const [showButton, setShowButton] = useState(false);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    const onScroll = () => {
      const shouldShow = window.pageYOffset > 400;
      setShowButton((prev) => (prev !== shouldShow ? shouldShow : prev));
    };
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
  <Container maxWidth={false} disableGutters sx={{ p: 0, m: 0 }}>
      <Suspense fallback={<LoadingScreen />}>
        <Routes>
          {generalRoutes.map((route, index) => (
            <Route key={index} path={route.path} element={route.element} />
          ))}
        </Routes>
      </Suspense>
      <Fade in={showButton}>
        <Zoom in={showButton} timeout={1000} unmountOnExit>
          <StyledFab
            onClick={scrollToTop}
            color="secondary"
            size="small"
            aria-label="scroll back to top"
          >
            <KeyboardArrowUpIcon />
          </StyledFab>
        </Zoom>
      </Fade>
    </Container>
  );
};

const Main = () => {
  const [user] = useContext(UserContext);
  const { mode, toggleColorMode } = useContext(ThemeContext);
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up("lg"));
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  const handleSidebarOpen = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleMobileOpen = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleProfileMenuClose();
    try {
      await firebase.auth().signOut();
      // Navigate to homepage after logout
      navigate("/", { replace: true });
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const isActiveRoute = (path) => {
    return location.pathname === path;
  };
  const gamificationEnabled = user?.preferences?.disableGamificationFeatures === undefined
    ? false
    : !user.preferences.disableGamificationFeatures;

  const menuItems = [
    { text: "Home", icon: <Home />, path: "/", roles: ["admin", "user"] },
    { text: "Past timers", icon: <History />, path: "/past", roles: ["admin", "user"] },
    { text: "Planning", icon: <Event />, path: "/planning", roles: ["admin", "user"] },
    { text: "Tags", icon: <Label />, path: "/tags", roles: ["admin", "user"] },
    { text: "Point multipliers", icon: <Calculate />, path: "/multipliers", roles: ["admin", "user"], gamificationOnly: true },
    { text: "Settings", icon: <Settings />, path: "/settings", roles: ["admin", "user"] },
  // Posts removed; trim admin shortcut to reduce surface
  ];

  const isAuthed = !!user;
  if (!isAuthed) {
    return <RenderUnauthScreens />;
  }

  const drawerContent = (
    <Box sx={{ width: 250, pt: 2, px: 1, height: "100%" }}>
      <List>
        {menuItems.map((item) => {
          if (!item.roles.includes(user?.role)) return null;
          if (item.gamificationOnly && !gamificationEnabled) return null;
          
          return (
            <ListItem key={item.text} disablePadding sx={{ mb: 1 }}>
              <ListItemButton
                component={RouterLink}
                to={item.path}
                selected={isActiveRoute(item.path)}
                onClick={() => {
                  if (!matches) setMobileOpen(false);
                }}
                sx={(theme) => ({
                  minHeight: 48,
                  borderRadius: 2,
                  px: 1.5,
                  color: theme.palette.text.secondary,
                  border: "1px solid transparent",
                  transition: theme.transitions.create(
                    ["background-color", "border-color", "color", "box-shadow"],
                    { duration: theme.transitions.duration.shorter },
                  ),
                  "& .MuiListItemIcon-root": {
                    minWidth: 40,
                    color: "inherit",
                    opacity: 0.85,
                  },
                  "&:hover": {
                    color: theme.palette.text.primary,
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? alpha(theme.palette.primary.light, 0.12)
                        : alpha(theme.palette.primary.main, 0.08),
                    borderColor:
                      theme.palette.mode === "dark"
                        ? alpha(theme.palette.primary.light, 0.26)
                        : alpha(theme.palette.primary.main, 0.12),
                  },
                  "&.Mui-selected": {
                    color:
                      theme.palette.mode === "dark"
                        ? theme.palette.common.white
                        : theme.palette.primary.dark,
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? alpha(theme.palette.primary.main, 0.22)
                        : alpha(theme.palette.primary.main, 0.12),
                    borderColor:
                      theme.palette.mode === "dark"
                        ? alpha(theme.palette.primary.light, 0.4)
                        : alpha(theme.palette.primary.main, 0.18),
                    boxShadow:
                      theme.palette.mode === "dark"
                        ? `0 12px 24px ${alpha(theme.palette.common.black, 0.24)}`
                        : "none",
                    "& .MuiListItemIcon-root": {
                      opacity: 1,
                    },
                    "&:hover": {
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? alpha(theme.palette.primary.main, 0.28)
                          : alpha(theme.palette.primary.main, 0.16),
                    },
                  },
                })}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );

  const isRunningRoute = location.pathname === "/running";

  return (
    <Box sx={{ display: "flex" }}>
      {!isRunningRoute && (
      <AppBar
        position="fixed"
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          backgroundColor:
            theme.palette.mode === "dark"
              ? alpha(theme.palette.grey[900], 0.88)
              : theme.palette.primary.main,
          color:
            theme.palette.mode === "dark"
              ? theme.palette.text.primary
              : theme.palette.primary.contrastText,
          backdropFilter: theme.palette.mode === "dark" ? "blur(18px)" : "none",
          boxShadow:
            theme.palette.mode === "dark"
              ? `0 10px 30px ${alpha(theme.palette.common.black, 0.28)}`
              : undefined,
          borderBottom:
            theme.palette.mode === "dark"
              ? `1px solid ${alpha(theme.palette.common.white, 0.08)}`
              : "none",
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={matches ? handleSidebarOpen : handleMobileOpen}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Timer.Quest
          </Typography>
          
          <IconButton
            onClick={toggleColorMode}
            color="inherit"
            aria-label="toggle dark mode"
          >
            {mode === "dark" ? <Brightness7 /> : <Brightness4 />}
          </IconButton>
          
          <IconButton
            size="large"
            edge="end"
            aria-label="account of current user"
            aria-haspopup="true"
            onClick={handleProfileMenuOpen}
            color="inherit"
          >
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor:
                  theme.palette.mode === "dark"
                    ? alpha(theme.palette.primary.light, 0.22)
                    : alpha(theme.palette.common.white, 0.2),
                color:
                  theme.palette.mode === "dark"
                    ? theme.palette.text.primary
                    : theme.palette.primary.contrastText,
                border:
                  theme.palette.mode === "dark"
                    ? `1px solid ${alpha(theme.palette.primary.light, 0.3)}`
                    : "none",
              }}
            >
              {user?.email?.charAt(0).toUpperCase()}
            </Avatar>
          </IconButton>
          
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            PaperProps={{
              elevation: 0,
              sx: {
                mt: 1.25,
                minWidth: 190,
                overflow: "hidden",
                border: "1px solid",
                borderColor: "divider",
                backgroundColor: (menuTheme) =>
                  menuTheme.palette.mode === "dark"
                    ? alpha(menuTheme.palette.background.paper, 0.98)
                    : menuTheme.palette.background.paper,
                backdropFilter: (menuTheme) =>
                  menuTheme.palette.mode === "dark" ? "blur(18px)" : "none",
                boxShadow: (menuTheme) =>
                  menuTheme.palette.mode === "dark"
                    ? `0 20px 48px ${alpha(menuTheme.palette.common.black, 0.34)}`
                    : menuTheme.shadows[8],
                "& .MuiMenuItem-root": {
                  gap: 1,
                  py: 1,
                  borderRadius: 1.5,
                },
              },
            }}
            MenuListProps={{ sx: { p: 0.75 } }}
          >
            <MenuItem component={RouterLink} to="/settings" onClick={handleProfileMenuClose}>
              <Settings sx={{ mr: 1 }} />
              Settings
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <Logout sx={{ mr: 1 }} />
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      )}

      {!isRunningRoute && (
      <Drawer
        variant={matches ? "persistent" : "temporary"}
        open={matches ? sidebarOpen : mobileOpen}
        onClose={handleMobileOpen}
        sx={{
          width: 250,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: 250,
            boxSizing: "border-box",
            borderRight: "1px solid",
            borderColor: "divider",
            backgroundColor: (drawerTheme) =>
              drawerTheme.palette.mode === "dark"
                ? alpha(drawerTheme.palette.grey[900], 0.96)
                : drawerTheme.palette.background.paper,
            backgroundImage: (drawerTheme) =>
              drawerTheme.palette.mode === "dark"
                ? `linear-gradient(180deg, ${alpha(drawerTheme.palette.primary.dark, 0.38)} 0%, ${alpha(
                    drawerTheme.palette.background.paper,
                    0.98,
                  )} 22%, ${drawerTheme.palette.background.paper} 100%)`
                : "none",
          },
        }}
      >
        <Toolbar />
        {drawerContent}
      </Drawer>
      )}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: isRunningRoute ? 0 : 3,
          transition: theme.transitions.create("margin", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          marginLeft: matches && sidebarOpen ? 0 : 0,
        }}
      >
        {!isRunningRoute && <Toolbar />}
        <PlanningRolloverProvider fallback={<LoadingScreen />}>
          <Suspense fallback={<LoadingScreen />}>
            <LoggedInRouter />
          </Suspense>
          <TimezoneMismatchPrompt />
        </PlanningRolloverProvider>
      </Box>
    </Box>
  );
};

// Main App component that wraps everything with BrowserRouter and AuthGuard
const App = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary FallbackComponent={GeneralErrorPage}>
        <AuthGuard>
          <Main />
        </AuthGuard>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default App;
